// AN-6 — quando si chiede il cookie che apre la CDN delle animazioni.
//
// ⛔ Qui vive la POLITICA, non il trasporto: quando rinnovare, come non
// chiedere due volte insieme, cosa fare se la richiesta fallisce. Il trasporto
// lo inietta chi monta, perché i due mount non lo fanno allo stesso modo — il
// browser ha bisogno di `credentials: "include"` per accettare il Set-Cookie,
// il telefono no. Due copie di questa politica divergerebbero al primo
// ripensamento sulla scadenza.

export const ANIMATION_ACCESS_PATH = "/api/animations/access";

/** Cosa risponde la porta: quando scade il cookie che ha appena messo. */
export type AnimationAccessGrant = { expiresAt: string };

// ⭐ Si rinnova un giorno PRIMA della scadenza, non alla scadenza. Il cookie
// dura sette giorni: rinnovare all'ultimo momento vorrebbe dire che chi apre
// l'app in palestra, senza campo, si trova le animazioni spente proprio mentre
// gli servono. Un giorno di margine copre una sessione senza rete.
const RENEW_BEFORE_MS = 24 * 60 * 60 * 1000;

export type AnimationAccess = {
  /** Assicura un cookie valido. ⛔ Non rilancia: le animazioni sono un extra,
   *  e un errore qui non deve mai fermare ciò che il chiamante stava facendo. */
  ensure: () => Promise<void>;
  /** Il prossimo `ensure` rinnova comunque. Da chiamare su un 403 di un asset:
   *  vuol dire che il cookie è scaduto o è stato revocato prima del previsto. */
  invalidate: () => void;
  /** `invalidate` e poi `ensure`, in quest'ordine: chiede un cookie nuovo
   *  ADESSO. È la cura di AN-7, per chi vede un'animazione fallire con l'app
   *  aperta. ⛔ Non rilancia, come `ensure`. */
  renew: () => Promise<void>;
};

export const createAnimationAccess = (
  request: () => Promise<AnimationAccessGrant>,
  // ⛔ Il reporter si INIETTA invece di chiamare `console` qui dentro: questo
  // pacchetto è puro e non ha né DOM né Node fra le sue lib — il typecheck lo
  // ha rifiutato, e aveva ragione. Chi monta sa dove finiscono i suoi log.
  onError?: (err: unknown) => void,
): AnimationAccess => {
  let validUntilMs = 0;
  // ⛔ Una richiesta sola alla volta: il prefetch scalda decine di URL in
  // parallelo e senza questo ognuno ne aprirebbe una.
  let inFlight: Promise<void> | null = null;

  const ensure = (): Promise<void> => {
    if (Date.now() < validUntilMs - RENEW_BEFORE_MS) return Promise.resolve();
    if (inFlight) return inFlight;

    inFlight = request()
      .then((grant) => {
        const parsed = Date.parse(grant.expiresAt);
        // Una scadenza illeggibile non è una scadenza: si riprova al giro dopo
        // invece di fidarsi di un NaN, che passerebbe ogni confronto.
        validUntilMs = Number.isFinite(parsed) ? parsed : 0;
      })
      .catch((err) => {
        // ⛔ Silenzioso per l'ATLETA, non per chi legge i log: un guasto
        // persistente degrada a «animazioni mai», e senza una riga nessuno lo
        // distinguerebbe da «questo esercizio non ne ha».
        onError?.(err);
        // ⛔ Silenzio DELIBERATO, e vale la pena dire perché: chi chiama sta
        // scaldando delle animazioni o sta aprendo una scheda esercizio. Se la porta
        // non risponde, l'animazione non si vede — che è già lo stato normale
        // per la maggior parte del catalogo — e nient'altro deve rompersi.
        validUntilMs = 0;
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  };

  const invalidate = () => {
    validUntilMs = 0;
  };

  return {
    ensure,
    invalidate,
    renew: () => {
      invalidate();
      return ensure();
    },
  };
};
